/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CacheType, createObservableDataAction, getCatalogId, IAction, IActionContext, IActionInput } from '@msdyn365-commerce/core';
import {
    ProductPrice,
    ProductsDataActions,
    ProductSearchResult,
    ProjectionDomain,
    AffiliationLoyaltyTier
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { ProductSearchResultExpanded } from '../shared/classes/ProductSearchResultExpanded';
import { getAffiliation } from '../shared/functions/get-affiliation';
import { ProductResultWithSwatchLimit } from '../modules/ext-search-result-container/actions/get-full-products-by-collection';

/**
 * Input class for the GetPriceForProductSearchResult Data Action.
 */
export class PriceForProductSearchResultInput implements IActionInput {
    public products: ProductSearchResultExpanded[];

    public constructor(products: ProductSearchResult[]) {
        this.products = products;
    }

    /**
     * Get the cache key.
     * @returns The cache key.
     */
    public getCacheKey = (): string => `PriceForProductSearchResult-${JSON.stringify(this.products)}`;

    /**
     * Get the cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'ProductSearchResult';

    /**
     * Define the data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetPriceForProductSearchResultDataAction.
 * @param products -- The product search result.
 * @returns The input.
 */
export const createActivePriceForProductSearchResultInput = (products: ProductSearchResultExpanded[]): PriceForProductSearchResultInput => {
    return new PriceForProductSearchResultInput(products);
};

/**
 * The Action Method for the GetPriceForProductSearchResult Data Action
 * Gets the active price for product search result via the getActivePrice RetailServer API.
 * @param input -- The data input.
 * @param context -- The action context.
 * @returns The product search result updated with price.
 */
export async function getPriceForProductSearchResultAction(
    input: PriceForProductSearchResultInput,
    context: IActionContext
): Promise<ProductSearchResultExpanded[]> {
    let productRecIds: number[];
    if (ArrayExtensions.hasElements(input.products)) {
        productRecIds = input.products.map(product => product.RecordId);
        //Remove duplicate ids from variant splitting
        productRecIds = productRecIds.filter((item, index, array) => array.indexOf(item) === index);
    } else {
        return Promise.resolve([]);
    }

    const catalogId = getCatalogId(context.requestContext);

    const projectDomain: ProjectionDomain = { ChannelId: +context.requestContext.apiSettings.channelId, CatalogId: catalogId };
    const priceZero = 0;

    var affiliation: AffiliationLoyaltyTier[] = getAffiliation(
        context.requestContext.locale,
        context.requestContext.app.config.affiliationIds,
        context.requestContext.app.config.loyaltyTierIds
    );

    try {
        const productPrices: ProductPrice[] = await ProductsDataActions.getActivePricesAsync(
            { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            affiliation,
            true,
            false,
            true
        );

        const productResults: ProductSearchResultExpanded[] = input.products.map(product => {
            const activePrice = productPrices.find(price => price.ProductId === product.RecordId);
            if (activePrice) {
                product.Price = activePrice.CustomerContextualPrice ?? product.Price;
                product.BasePrice = Math.max(
                    activePrice.BasePrice ?? priceZero,
                    activePrice.TradeAgreementPrice ?? priceZero,
                    activePrice.AdjustedPrice ?? priceZero
                );
                product.ProductPrice = activePrice;
            }
            return product;
        });

        return Promise.resolve(productResults);
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- existing error type.
        if (error instanceof Error) {
            context.telemetry.exception(error);
            context.telemetry.debug('[getPriceForProductSearchResultAction]Error executing action');
            context.telemetry.debug(error.message);
        }
        throw new Error('[getPriceForProductSearchResultAction]Error executing action');
    }
}

/**
 * The getPriceForProductSearchResult data action.
 */
export const getPriceForProductSearchResultDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-price-for-product-search-result',
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- existing pattern.
    action: <IAction<ProductResultWithSwatchLimit[]>>getPriceForProductSearchResultAction,
    input: createActivePriceForProductSearchResultInput
});

export default getPriceForProductSearchResultDataAction;
